/**
 * CameraController.ts — pointer-driven orbital controls.
 *
 * Bindings:
 *   - LMB drag      → orbit (azimuth, elevation)
 *   - Wheel         → zoom (multiplicative on distance)
 *   - RMB drag      → pan target in screen plane (preserving height)
 *
 * Stage 4 will add Chase / Cockpit / Free / External camera modes.
 */

import type { Camera } from "./Camera";

export class OrbitalCameraController {
  private dragging: "orbit" | "pan" | null = null;
  private lastX = 0;
  private lastY = 0;

  constructor(
    private element: HTMLElement,
    private camera: Camera,
  ) {
    element.addEventListener("pointerdown", this.onDown);
    element.addEventListener("pointermove", this.onMove);
    element.addEventListener("pointerup", this.onUp);
    element.addEventListener("pointercancel", this.onUp);
    element.addEventListener("wheel", this.onWheel, { passive: false });
    element.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  private onDown = (e: PointerEvent) => {
    this.element.setPointerCapture(e.pointerId);
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    if (e.button === 0) this.dragging = "orbit";
    else if (e.button === 2) this.dragging = "pan";
  };

  private onUp = (e: PointerEvent) => {
    this.dragging = null;
    if (this.element.hasPointerCapture(e.pointerId)) {
      this.element.releasePointerCapture(e.pointerId);
    }
  };

  private onMove = (e: PointerEvent) => {
    if (!this.dragging) return;
    const dx = e.clientX - this.lastX;
    const dy = e.clientY - this.lastY;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    const cam = this.camera;
    if (this.dragging === "orbit") {
      const orbitSpeed = 0.005;
      cam.azimuth -= dx * orbitSpeed;
      cam.elevation += dy * orbitSpeed;
      const limit = Math.PI * 0.5 - 0.05;
      cam.elevation = Math.max(-limit, Math.min(limit, cam.elevation));
      cam.recompute();
    } else if (this.dragging === "pan") {
      // Pan target in the camera's right/up plane, scaled by distance for natural feel.
      const k = cam.distance * 0.0015;
      // Build right and up from current camera basis.
      const fwd = cam.forward;
      const worldUp: [number, number, number] = [0, 1, 0];
      const right: [number, number, number] = [
        fwd[1] * worldUp[2] - fwd[2] * worldUp[1],
        fwd[2] * worldUp[0] - fwd[0] * worldUp[2],
        fwd[0] * worldUp[1] - fwd[1] * worldUp[0],
      ];
      const rLen = Math.hypot(right[0], right[1], right[2]) || 1;
      right[0] /= rLen;
      right[1] /= rLen;
      right[2] /= rLen;
      const up: [number, number, number] = [
        right[1] * fwd[2] - right[2] * fwd[1],
        right[2] * fwd[0] - right[0] * fwd[2],
        right[0] * fwd[1] - right[1] * fwd[0],
      ];
      cam.target[0] += (-right[0] * dx + up[0] * dy) * k;
      cam.target[1] += (-right[1] * dx + up[1] * dy) * k;
      cam.target[2] += (-right[2] * dx + up[2] * dy) * k;
      cam.recompute();
    }
  };

  private onWheel = (e: WheelEvent) => {
    e.preventDefault();
    // Scale step by current distance so close-up scroll is fine and
    // far-away scroll covers ground. `deltaMode === 1` (lines) reports
    // ~3 per notch, `deltaMode === 0` (pixels) reports ~100 — normalise.
    const dy =
      e.deltaMode === 1
        ? e.deltaY * 33
        : e.deltaMode === 2
          ? e.deltaY * 800
          : e.deltaY;
    const factor = Math.exp(dy * 0.0025);
    // Allow very close inspection (down to 5 cm) and very wide overviews.
    // Upper bound stays inside the camera's far plane so the target
    // doesn't get clipped when fully zoomed out.
    const maxDist = Math.max(100, this.camera.far * 0.95);
    this.camera.distance = Math.max(
      0.05,
      Math.min(maxDist, this.camera.distance * factor),
    );
    this.camera.recompute();
  };
}
